import { type ChangeEvent, type FormEvent, useRef, useState } from "react";
import { FileText, FileUp } from "lucide-react";
import CleButton from "../../../components/CleButton";
import CleModal, {
  CleModalRoot,
  CleModalTrigger,
} from "../../../components/CleModal";
import { useCreateLegalAid } from "@/hooks/useLegalAid";
import {
  MAX_LESSONS_PER_EVIDENCE,
  isValidLessonCount,
} from "../../../utils/lessonLimits";
import { getCleUploadErrorMessage } from "../../../utils/getCleUploadErrorMessage";

const LEGAL_AID_FORM_ID = "cle-legal-aid-upload-form";

const LegalAidUploadDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [numberOfLessons, setNumberOfLessons] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const createLegalAidMutation = useCreateLegalAid();

  const resetForm = () => {
    setFile(null);
    setNumberOfLessons("");
    setValidationError(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);

    if (open) {
      createLegalAidMutation.reset();
      setValidationError(null);

      return;
    }

    resetForm();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFile(event.target.files?.[0] ?? null);
    setValidationError(null);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const lessons = Number(numberOfLessons);

    if (!file) {
      setValidationError("Vælg en PDF-fil med dokumentationen.");

      return;
    }

    if (!isValidLessonCount(lessons)) {
      setValidationError(
        `Angiv et antal lektioner mellem 1 og ${MAX_LESSONS_PER_EVIDENCE}. Decimaler er tilladt og afrundes til to decimaler.`,
      );

      return;
    }

    createLegalAidMutation.mutate(
      {
        file,
        numberOfLessons: lessons,
      },
      {
        onSuccess: () => {
          setIsOpen(false);
          resetForm();
        },
      },
    );
  };

  return (
    <CleModalRoot
      controller={{
        open: isOpen,
        setOpen: setIsOpen,
        onOpenChange: handleOpenChange,
      }}
    >
      <CleModalTrigger asChild>
        <CleButton id="cle-upload-legal-aid" variant="outline" size="sm">
          <FileUp size={17} strokeWidth={2} />
          Opret dokumentation
        </CleButton>
      </CleModalTrigger>
      <CleModal
        title="Opret advokatvagt/retshjælp"
        description="Upload skriftlig erklæring fra ledelse og angiv antal lektioner."
        size="sm"
        footer={
          <div className="d-flex justify-content-center gap-3 w-100 px-3 py-2">
            <CleButton
              id="cle-cancel-legal-aid-upload"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={createLegalAidMutation.isPending}
            >
              Afbryd
            </CleButton>
            <CleButton
              id="cle-submit-legal-aid-upload"
              type="submit"
              form={LEGAL_AID_FORM_ID}
              disabled={createLegalAidMutation.isPending}
            >
              {createLegalAidMutation.isPending ? "Opretter..." : "Opret"}
            </CleButton>
          </div>
        }
      >
        <form
          id={LEGAL_AID_FORM_ID}
          className="d-flex flex-column gap-3"
          onSubmit={handleSubmit}
        >
          <div className="d-flex flex-column align-items-center text-center gap-3">
            <span
              className="cle-compliance-doc-dashboard__empty-icon mb-0"
              aria-hidden="true"
            >
              <FileText size={24} strokeWidth={1.75} />
            </span>
            <div>
              <h3 className="fs-5 fw-semibold mb-2">Upload dokumentation</h3>
              <p className="text-muted mb-0">
                Du kan kun uploade PDF-filer. Angiv højst{" "}
                {MAX_LESSONS_PER_EVIDENCE} lektioner pr. dokument.
              </p>
            </div>
          </div>

          <div>
            <label
              className="form-label fw-semibold"
              htmlFor="cle-legal-aid-file"
            >
              Dokumentation
            </label>
            <input
              ref={fileInputRef}
              id="cle-legal-aid-file"
              className="form-control"
              type="file"
              accept="application/pdf,.pdf"
              onChange={handleFileChange}
              disabled={createLegalAidMutation.isPending}
            />
          </div>

          <div>
            <label
              className="form-label fw-semibold"
              htmlFor="cle-legal-aid-lessons"
            >
              Antal lektioner
            </label>
            <input
              id="cle-legal-aid-lessons"
              className="form-control"
              type="number"
              inputMode="decimal"
              min="1"
              max={MAX_LESSONS_PER_EVIDENCE}
              step="any"
              value={numberOfLessons}
              onChange={(event) => {
                setNumberOfLessons(event.target.value);
                setValidationError(null);
              }}
              disabled={createLegalAidMutation.isPending}
            />
          </div>

          {validationError ? (
            <div className="alert alert-warning mb-0" role="alert">
              {validationError}
            </div>
          ) : null}

          {createLegalAidMutation.isError ? (
            <div className="alert alert-warning mb-0" role="alert">
              {getCleUploadErrorMessage(
                createLegalAidMutation.error,
                "Dokumentationen kunne ikke oprettes lige nu. Prøv igen.",
              )}
            </div>
          ) : null}
        </form>
      </CleModal>
    </CleModalRoot>
  );
};

export default LegalAidUploadDialog;
